// ---------------------------------------------------------------------------
// MULTI-FILIAIS: "Condomínios em destaque" — fileira de chips logo abaixo da
// busca do hero, com os empreendimentos DA FILIAL ativa.
//
// Fonte: `init_props.home_ventures`, que no SSR já sai recortado pelas cidades
// da filial (ver filtraEmpreendimentosRegiao em lib/region). REGRA (Daniel 14/10):
// SÓ os empreendimentos com `tbl_empreendimento.Destaque = 'sim'` (checkbox
// "Destaque (aparece em primeiro)" do CRM; as flags legadas DestaqueSite/
// DestaqueBanner NÃO contam — campo `destaque` do slim), até 10, na ordem do
// carrossel (slimHomeVentures). Nº de unidades NÃO ordena mais. Nenhum fetch novo.
//
// PORTÃO: `modoPopup` + `site.exibir_empreendimento` + 1 ou mais destaques na
// filial. Fora disso devolve null (zero markup) — filial sem destaque marcado
// fica sem a faixa (decisão do Daniel: não cai em regra antiga).
// ---------------------------------------------------------------------------
import Link from 'next/link';
import { useMemo } from 'react';
import { HiMapPin } from 'react-icons/hi2';
import { useRegiao, useRegiaoHref } from '../../context/Info';
import { isColorLight } from '../../../utils/FunctionsGlobals';
import { appendCodsiteQuery } from '../../../utils/url';
import { paginaInternaAtelier, varsAtelier } from '../atelier/atelier';
import { ehModeloAurora, varsAurora } from '../aurora/aurora';
import { ehModeloHorizonte, varsHorizonteInterna } from '../horizonte/horizonte';
import { ehModeloInternacional, semCaixaAlta } from '../internacional/internacional';
import { ehModeloPanorama, varsPanorama } from '../panorama/panorama';
import { ehModeloMetropole, varsMetropole } from '../metropole/metropole';
import styles from './CondominiosChips.module.css';

const MAX_CHIPS = 10;

const CondominiosChips: React.FC<{
    site: any;
    /** recorte enxuto do SSR (init_props.home_ventures), já por filial. */
    ventures?: any[] | null;
    codsite?: any;
    origin?: any;
}> = ({ site, ventures, codsite, origin }) => {
    const { modoPopup } = useRegiao();
    const withRegiao = useRegiaoHref();

    const lista = useMemo(() => {
        const itens = Array.isArray(ventures) ? ventures : [];
        // só os marcados como destaque, na ordem do carrossel (slimHomeVentures).
        return itens.filter((v) => v?.destaque === true).slice(0, MAX_CHIPS);
    }, [ventures]);

    if (!modoPopup || !site?.exibir_empreendimento) return null;
    // curadoria do cliente: 1 destaque marcado já vale a faixa; nenhum = sem faixa.
    if (!lista.length) return null;

    // Atelier (topo 12): faixa em papel com os chips na linguagem do modelo (linha fina, versalete).
    const atelier = paginaInternaAtelier(site);
    // Aurora (topo 10) / Panorama (topo 13): chips na linguagem do modelo (vars --au-* / --pn-* + classe).
    const aurora = ehModeloAurora(site);
    const panorama = ehModeloPanorama(site);
    // Horizonte (topo 11): faixa na linguagem do modelo (vars --hz-* + classe), nas laterais da régua da home.
    const horizonte = ehModeloHorizonte(site);
    // Internacional (topo 16): seção do modelo no cinza do "lábio" do herói — título 800 como as seções, chips em
    // pílula com o pino num círculo, na régua --in-margem da home (vars --in-* vêm da raiz .home).
    const internacional = ehModeloInternacional(site);
    // Metrópole (topo 15): faixa BRANCA com chips brancos de borda fina, ícone no acento, nome 14px/600 + cidade 12px
    // cinza e título leve (vars --mt-* + classe), na régua de 1280 da home do modelo.
    const metropole = ehModeloMetropole(site);
    const fundoClaro = isColorLight(site?.cor5);
    const cores = internacional ? ({
        '--chip-accent': 'var(--in-base)',
        '--chip-borda': 'var(--in-borda)',
        '--chip-fundo': '#ffffff',
        // a pílula sólida do hover vem do CSS só com ponteiro; no toque o :hover "grudado" fica branco (texto legível)
        '--chip-fundo-hover': '#ffffff',
    } as React.CSSProperties) : metropole ? ({
        ...varsMetropole(site),
        '--chip-accent': 'var(--mt-acento)',
        '--chip-borda': 'var(--mt-borda)',
        '--chip-fundo': '#ffffff',
        '--chip-fundo-hover': 'var(--mt-acento-suave)',
    } as React.CSSProperties) : atelier ? varsAtelier(site) : horizonte ? ({
        ...(varsHorizonteInterna(site) || {}),
        '--chip-accent': 'var(--hz-acento)',
        '--chip-borda': 'var(--hz-sutil-forte)',
        '--chip-fundo': 'transparent',
        '--chip-fundo-hover': 'var(--hz-painel)',
    } as React.CSSProperties) : aurora ? ({
        ...varsAurora(site),
        '--chip-accent': 'var(--au-acento)',
        '--chip-borda': 'var(--au-sutil-forte)',
        '--chip-fundo': 'var(--au-painel)',
        '--chip-fundo-hover': 'var(--au-sutil)',
    } as React.CSSProperties) : panorama ? ({
        ...varsPanorama(site),
        '--chip-accent': 'var(--pn-vivo)',
        '--chip-borda': fundoClaro ? 'rgba(0,25,51,0.18)' : 'rgba(255,255,255,0.3)',
        '--chip-fundo': fundoClaro ? 'rgba(0,25,51,0.04)' : 'rgba(255,255,255,0.08)',
        '--chip-fundo-hover': 'var(--pn-vivo-suave)',
    } as React.CSSProperties) : {
        '--chip-accent': site?.cor2 || site?.cor1 || 'currentColor',
        '--chip-borda': fundoClaro ? 'rgba(0,0,0,0.16)' : 'rgba(255,255,255,0.28)',
        '--chip-fundo': fundoClaro ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.08)',
        '--chip-fundo-hover': fundoClaro ? 'rgba(0,0,0,0.07)' : 'rgba(255,255,255,0.16)',
    } as React.CSSProperties;

    return (
        <section
            className={`${styles.faixa}${atelier ? ` ${styles.atelier}` : ''}${aurora ? ` ${styles.aurora}` : ''}${panorama ? ` ${styles.panorama}` : ''}${horizonte ? ` ${styles.horizonte}` : ''}${metropole ? ` ${styles.metropole}` : ''}${internacional ? ` ${styles.internacional}` : ''}`}
            style={cores}
            aria-labelledby="condominios-chips-titulo"
            data-el="condominios-chips"
        >
            <h2 className={styles.titulo} id="condominios-chips-titulo">
                {/* Horizonte: a linha curta do sobretítulo do modelo (mesmo DOM do CardHorizonte) */}
                {horizonte ? <span className={styles.tituloLinha} aria-hidden="true" /> : null}
                Condomínios em destaque
            </h2>
            <ul className={styles.linha}>
                {lista.map((venture: any) => (
                    <li key={venture?.id}>
                        <Link
                            prefetch={false}
                            href={withRegiao(appendCodsiteQuery(`/empreendimento/${venture?.id}`, codsite, origin))}
                            className={styles.chip}
                            title={`Ver o empreendimento ${venture?.nome}`}
                        >
                            <HiMapPin size={14} className={styles.icone} aria-hidden="true" />
                            <span className={styles.chipNome}>{internacional ? semCaixaAlta(venture?.nome) : venture?.nome}</span>
                            {venture?.cidade ? (
                                <span className={styles.chipCidade}>{venture.cidade}</span>
                            ) : null}
                        </Link>
                    </li>
                ))}
            </ul>
        </section>
    );
};

export default CondominiosChips;
